import { createFileRoute, Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useAbility } from '@features/authorization';
import {
  getUserProfilesDirectoryReturnSearch,
  markCrmCustomerDirectoryProfileReturn,
  userProfilesDirectorySortSchema,
} from '@features/crm-customer-directory';
import { isCrmCustomerDocumentFullPagePath } from '@features/crm-customer-documents';
import { isCrmCustomerLearningProfilePath } from '@features/crm-learning-profiles';
import { helpdeskSearchSchema } from '@features/helpdesk';
import {
  canAccessCrmCustomerDocuments,
  canAccessCrmUserProfilePayments,
  canAccessHelpdesk,
} from '@shared/lib';
import { Alert, AlertDescription, AlertTitle, Button, PageBackLink } from '@shared/ui';
import {
  CrmCustomerRecordShell,
  findCrmCustomerRecordArea,
} from '@widgets/crm-customer-record-shell';

import { crmCustomerRecordSearchSchema } from '../../-user-profile-search';

export const Route = createFileRoute('/$locale/_authenticated/crm/user-profiles/$userProfileId')({
  validateSearch: crmCustomerRecordSearchSchema,
  component: CrmCustomerRecordRoute,
});

function CrmCustomerRecordRoute() {
  const ability = useAbility();
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale: rawLocale } = Route.useParams();
  const locale = rawLocale as unknown as string;
  const search = Route.useSearch();
  const directorySearch = {
    accountLinkage: search.accountLinkage,
    businessLineKeys: search.businessLineKeys,
    certificationTypeIds: search.certificationTypeIds,
    cities: search.cities,
    currentMembershipStatuses: search.currentMembershipStatuses,
    cursor: search.cursor,
    freshnessState: search.freshnessState,
    freshnessStates: search.freshnessStates,
    linkedSystems: search.linkedSystems,
    mainLocationIds: search.mainLocationIds,
    membershipTypeIds: search.membershipTypeIds,
    page: search.page,
    preferredLanguages: search.preferredLanguages,
    q: search.q,
    regions: search.regions,
    roles: search.roles,
    profileOrigins: search.profileOrigins,
    shopifyCustomerId: search.shopifyCustomerId,
    shopifyId: search.shopifyId,
    sort: userProfilesDirectorySortSchema.safeParse(search.sort).data,
    userOrigins: search.userOrigins,
    view: search.view,
  };
  const directoryReturnSearch = getUserProfilesDirectoryReturnSearch(directorySearch);
  const courseReturnTo = search.returnTo;
  const membershipReturnTo = search.membershipReturnTo;
  const customerRecordSearch = {
    ...directorySearch,
    combinedSearchReturnTo: search.combinedSearchReturnTo,
    ...helpdeskSearchSchema.parse(search),
    helpdeskReturnTo: search.helpdeskReturnTo,
    membershipDateFrom: search.membershipDateFrom,
    membershipDateTo: search.membershipDateTo,
    membershipInstructorId: search.membershipInstructorId,
    membershipPage: search.membershipPage,
    membershipQ: search.membershipQ,
    membershipReturnTo,
    membershipStatus: search.membershipStatus,
    membershipTypeId: search.membershipTypeId,
    learningItemId: search.learningItemId,
    learningCertificationTypeId: search.learningCertificationTypeId,
    learningCertifiedFrom: search.learningCertifiedFrom,
    learningCertifiedTo: search.learningCertifiedTo,
    learningAttemptedFrom: search.learningAttemptedFrom,
    learningAttemptedTo: search.learningAttemptedTo,
    learningCostMax: search.learningCostMax,
    learningCostMin: search.learningCostMin,
    learningCursor: search.learningCursor,
    learningCursorHistory: search.learningCursorHistory,
    learningCourseId: search.learningCourseId,
    learningCourseStatus: search.learningCourseStatus,
    learningCourseTypeId: search.learningCourseTypeId,
    learningDateFrom: search.learningDateFrom,
    learningDateTo: search.learningDateTo,
    learningInstructorId: search.learningInstructorId,
    learningInstructorPaymentStatus: search.learningInstructorPaymentStatus,
    learningPage: search.learningPage,
    learningQ: search.learningQ,
    learningRegistrationId: search.learningRegistrationId,
    learningRegistrationReferenceType: search.learningRegistrationReferenceType,
    learningRegistrationType: search.learningRegistrationType,
    learningResult: search.learningResult,
    learningSection: search.learningSection,
    learningSort: search.learningSort,
    learningSchoolId: search.learningSchoolId,
    learningStudentPaymentStatus: search.learningStudentPaymentStatus,
    learningSubmittedFrom: search.learningSubmittedFrom,
    learningSubmittedTo: search.learningSubmittedTo,
    learningType: search.learningType,
    trainingDateStartedFrom: search.trainingDateStartedFrom,
    trainingDateStartedTo: search.trainingDateStartedTo,
    trainingCursor: search.trainingCursor,
    trainingCursorHistory: search.trainingCursorHistory,
    trainingFlow: search.trainingFlow,
    trainingQ: search.trainingQ,
    trainingProgramIds: search.trainingProgramIds,
    trainingStatus: search.trainingStatus,
    documentFileState: search.documentFileState,
    documentPage: search.documentPage,
    documentQ: search.documentQ,
    documentRegistrationId: search.documentRegistrationId,
    documentStatus: search.documentStatus,
    documentSort: search.documentSort,
    documentType: search.documentType,
    paymentQ: search.paymentQ,
    returnTo: courseReturnTo,
  };
  const { t } = useTranslation();
  // Outlet renders committed matches while the next location is still loading.
  // Keep its Customer Record provider mounted until those matches change together.
  const pathname = useRouterState({
    select: (state) => state.matches.at(-1)?.pathname ?? state.location.pathname,
  });
  const customerPathMarker = `/user-profiles/${customerId}`;
  const customerPath = pathname.slice(
    pathname.indexOf(customerPathMarker) + customerPathMarker.length
  );
  const requestedAreaKey = customerPath.replace(/^\/+|\/+$/g, '') || 'overview';
  const validNestedId =
    !requestedAreaKey.includes('/') ||
    requestedAreaKey === 'documents/new' ||
    z.uuid().safeParse(requestedAreaKey.split('/').at(-1)).success;
  const fullPage =
    z.uuid().safeParse(customerId).success &&
    validNestedId &&
    (isCrmCustomerDocumentFullPagePath(pathname, customerId) ||
      /^training\/[0-9a-f-]{36}$/i.test(requestedAreaKey) ||
      isCrmCustomerLearningProfilePath(pathname, customerId));
  const requestedRootArea = requestedAreaKey.split('/')[0];
  const activeArea = findCrmCustomerRecordArea(fullPage ? requestedRootArea : requestedAreaKey);
  const activeTabId =
    requestedAreaKey === 'helpdesk'
      ? 'communications'
      : requestedAreaKey === 'payments'
        ? 'payments'
        : activeArea?.id;

  const backAction =
    search.helpdeskReturnTo !== undefined ? (
      <PageBackLink
        onClick={() => {
          void navigate({ to: search.helpdeskReturnTo as never });
        }}
      >
        {t('common.back', 'Back')}
      </PageBackLink>
    ) : courseReturnTo !== undefined ? (
      <PageBackLink
        type="button"
        onClick={() => {
          void navigate({ to: courseReturnTo as never });
        }}
      >
        {t('crm.courseProfile.actions.backToCourse', 'Back to course')}
      </PageBackLink>
    ) : search.combinedSearchReturnTo !== undefined ? (
      <Button asChild variant="ghost" size="sm" className="h-auto w-fit px-0 py-0">
        <Link to={search.combinedSearchReturnTo as never}>
          <ArrowLeft aria-hidden="true" />
          {t('common.back', 'Back')}
        </Link>
      </Button>
    ) : membershipReturnTo === undefined ? (
      <Button asChild variant="ghost" size="sm" className="h-auto w-fit px-0 py-0">
        <Link
          to="/$locale/crm"
          params={{ locale }}
          search={directoryReturnSearch}
          onClick={markCrmCustomerDirectoryProfileReturn}
        >
          <ArrowLeft aria-hidden="true" />
          {t('crm.customerProfile.backToDirectory', 'Customer directory')}
        </Link>
      </Button>
    ) : (
      <Button asChild variant="ghost" size="sm" className="h-auto w-fit px-0 py-0">
        <Link to={membershipReturnTo as never}>
          <ArrowLeft aria-hidden="true" />
          {t('crm.customerProfile.backToMembership', 'Back to Memberships')}
        </Link>
      </Button>
    );

  if (!z.uuid().safeParse(customerId).success || activeTabId === undefined) {
    return (
      <main className="min-w-0 p-4 sm:p-6">
        <div className="space-y-4">
          {backAction}
          <Alert>
            <AlertTitle>{t('crm.customerRecord.invalidAreaTitle', 'Page not found')}</AlertTitle>
            <AlertDescription>
              {t(
                'crm.customerRecord.invalidAreaDescription',
                'This customer-record area does not exist.'
              )}
            </AlertDescription>
          </Alert>
        </div>
      </main>
    );
  }

  if (
    (requestedRootArea === 'documents' && !canAccessCrmCustomerDocuments(ability)) ||
    (requestedRootArea === 'payments' && !canAccessCrmUserProfilePayments(ability)) ||
    (requestedRootArea === 'helpdesk' && !canAccessHelpdesk(ability))
  ) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }
  if (fullPage) return <Outlet />;

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmCustomerRecordShell
        activeTabId={activeTabId}
        customerId={customerId}
        backAction={backAction}
        renderAreaLink={(area, children, { className, isActive }) => (
          <Link
            key={area.id}
            to={area.route}
            params={{ userProfileId: customerId, locale }}
            search={customerRecordSearch}
            className={className}
            aria-current={isActive ? 'page' : undefined}
            data-state={isActive ? 'active' : 'inactive'}
          >
            {children}
          </Link>
        )}
        renderAreaRedirect={(area) => (
          <Navigate
            to={area.route}
            params={{ userProfileId: customerId, locale }}
            search={customerRecordSearch}
            replace
            resetScroll={false}
          />
        )}
      >
        <Outlet />
      </CrmCustomerRecordShell>
    </main>
  );
}
